

import { fecha } from '@/shared/utilidades/formato'

import { useLoQueLevante, type Levantada } from './useLoQueLevante'
import estilos from './LoQueLevante.module.css'

/**
 * LO QUE LEVANTÉ Y QUEDÓ A MEDIAS.
 *
 * La pantalla del técnico se arma con lo que le ASIGNARON. Una orden que él
 * acaba de levantar en la calle no tiene nada de eso —no está aprobada, no hay
 * orden de trabajo, no hay cuadrilla— así que en cuanto cerraba el cotizador
 * desaparecía y no había por dónde volver a entrar.
 *
 * Y encadenar los tres pasos empeoró justamente eso: van seguidos y dan por
 * hecho que se terminan de una sentada. En la calle no pasa —llega el cliente,
 * suena el teléfono, se acaba la batería—. Un paso a paso sin forma de
 * retomarlo no es un paso a paso: es una sola oportunidad.
 *
 * Aquí está lo suyo sin terminar, diciendo EN QUÉ QUEDÓ, que es lo primero que
 * uno se pregunta al volver.
 */


/**
 * Qué le falta, dicho como se lo diría un compañero.
 *
 * `accion` en null es lo terminado: no hay nada que pedirle, y un botón ahí
 * inventaría un paso que no existe.
 */
function queFalta(l: Levantada): { rotulo: string; accion: string | null } {
  if (l.paso === 'sin_cotizar') return { rotulo: 'Sin precio', accion: 'Cotizar' }
  if (l.paso === 'borrador') return { rotulo: 'Cotización a medias', accion: 'Seguir cotizando' }
  if (l.paso === 'cotizada') return { rotulo: 'Falta la foto', accion: 'Tomar fotos' }
  // Gerencia la devolvió: se queda a la vista, y con qué hacerle.
  if (l.paso === 'rechazada') return { rotulo: 'Devuelta, hay que rehacerla', accion: 'Modificar' }
  // 'esperando': cotizada y con fotos, aguardando a gerencia. Nada que hacer.
  return { rotulo: 'Lista, esperando a gerencia', accion: null }
}

type Props = {
  onSeguir: (l: Levantada) => void
}

export type { Levantada }

export function LoQueLevante({ onSeguir }: Props) {
  const { data: levantadas = [] } = useLoQueLevante()

  // Nada a medias, nada que mostrar. Una lista de pendientes que nunca se
  // vacía deja de mirarse.
  if (levantadas.length === 0) return null

  return (
    <section className={estilos.zona}>
      <p className={estilos.titulo}>
        Lo que usted levantó
        <span className={estilos.cuantas}>{levantadas.length}</span>
      </p>

      <ul className={estilos.lista}>
        {levantadas.map((l) => {
          const falta = queFalta(l)

          return (
            <li key={l.orden_servicio_id} className={estilos.fila}>
              <span className={estilos.datos}>
                <b className={estilos.cliente}>{l.cliente}</b>
                <span className={estilos.donde}>{l.direccion ?? 'Sin dirección'}</span>
                <span className={estilos.falta}>
                  {falta.rotulo} · {l.numero} · {fecha(l.creado_en)}
                </span>
                {/* POR QUÉ LA DEVOLVIERON, para no rehacerla a ciegas. */}
                {l.paso === 'rechazada' && l.motivo_rechazo && (
                  <span className={estilos.motivo}>“{l.motivo_rechazo}”</span>
                )}
              </span>

              {falta.accion ? (
                <button type="button" className={estilos.seguir} onClick={() => onSeguir(l)}>
                  {falta.accion}
                </button>
              ) : (
                /* Terminado. Se marca y no se pide nada: la señal de que llegó
                   es justamente que siga aquí. */
                <span className={estilos.hecho} aria-label="Terminado">
                  ✓
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
